import { FC, memo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useResponsive } from '../../../../hooks';
import { Max } from '../max';
import { Telegram } from '../telegram';
import { Request } from '../request';
import { Accruals } from '../accruals';
import { Meters } from '../meters';
import { Rate } from '../rate';
import { Phone } from '../phone';
import {
  getPublicRequestData,
  getOnLoadPublicRequestState,
  getAccrualsData,
  getOnLoadPublicRequestAccrualsState,
  getOnLoadPublicRequestMetersState,
  getMetersData,
} from '../../selectors';
import { MIN_SKELETON_DURATION } from '../../constants';
import { VStack, HStack } from '../../components-v4';
import { Wrapper } from './body.styled';

export const Body: FC = memo(() => {
  const requestData = useSelector(getPublicRequestData);
  const accrualsData = useSelector(getAccrualsData);
  const metersData = useSelector(getMetersData);

  const onLoadRequest = useSelector(getOnLoadPublicRequestState);
  const onLoadAccrualsData = useSelector(getOnLoadPublicRequestAccrualsState);
  const onLoadMetersData = useSelector(getOnLoadPublicRequestMetersState);

  const [requestLoaded, setRequestLoaded] = useState<boolean>(false);
  const loadStartRef = useRef<number | null>(onLoadRequest ? Date.now() : null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frameId: number | null = null;

    const updateLayoutOffset = () => {
      const element = innerRef.current;

      if (!element) {
        return;
      }

      const left = element.getBoundingClientRect().left;

      document.documentElement.style.setProperty(
        '--body-offset-left',
        `${left}px`
      );
    };

    const handleResize = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(updateLayoutOffset);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);

      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    if (onLoadRequest) {
      loadStartRef.current = Date.now();
      setRequestLoaded(false);
    } else if (loadStartRef.current !== null) {
      const elapsed = Date.now() - loadStartRef.current;
      const remaining = MIN_SKELETON_DURATION - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => setRequestLoaded(true), remaining);

        return () => clearTimeout(timer);
      }

      setRequestLoaded(true);
    }
  }, [onLoadRequest]);

  const { viewport } = useResponsive();

  return (
    <Wrapper>
      <VStack ref={innerRef} className="inner-block" spacing={viewport.mobile ? 's' : 'm'}>
        {requestData.status === 'performed' && requestLoaded && <Rate />}
        <Request />
        <VStack spacing="xs">
          {!viewport.mobile && (
            <Phone
              phoneNumbers={requestData.dispatcher_phones}
              inHeader={false}
            />
          )}
          <HStack
            spacing="xs"
            style={{
              flexDirection: viewport.mobile ? 'column' : 'row',
            }}
          >
            <Max />
            <Telegram />
          </HStack>
        </VStack>
        {(onLoadAccrualsData || !!accrualsData.length) && <Accruals />}
        {(onLoadMetersData || !!metersData.length) && <Meters />}
      </VStack>
    </Wrapper>
  );
});
